import type { PropsWithChildren } from "react";
import { useEffect, useMemo, useRef } from "react";
import { TelemetryContextProvider } from "../shared/TelemetryContext";
import { initTelemetry, getTracer } from "./otel";

export interface TelemetryProviderProps {
    serviceName: string;
    environment?: string;
    collectorUrl?: string;
    serviceVersion?: string;
}

export const TelemetryProvider = ({
    children,
    serviceName,
    environment,
    collectorUrl,
    serviceVersion
}: PropsWithChildren<TelemetryProviderProps>): JSX.Element => {
    const shutdownRef = useRef<(() => Promise<void>) | undefined>();

    useEffect(() => {
        shutdownRef.current?.();
        shutdownRef.current = initTelemetry({
            serviceName,
            environment,
            collectorUrl,
            serviceVersion
        });

        return () => {
            if (shutdownRef.current) {
                void shutdownRef.current();
                shutdownRef.current = undefined;
            }
        };
    }, [serviceName, environment, collectorUrl, serviceVersion]);

    const contextValue = useMemo(
        () => {
            const tracer = getTracer();

            return {
                track: (eventName: string, payload?: Record<string, unknown>) => {
                    // Create a span for the event
                    const span = tracer.startSpan(eventName);

                    try {
                        // Add payload as attributes
                        if (payload) {
                            Object.entries(payload).forEach(([key, value]) => {
                                // Convert value to string/number/boolean for OpenTelemetry attributes
                                if (value !== null && value !== undefined) {
                                    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                                        span.setAttribute(key, value);
                                    } else if (typeof value === 'object') {
                                        // For objects, stringify or extract key properties
                                        if (key === 'error' && value instanceof Error) {
                                            span.setAttribute(`${key}.message`, value.message);
                                            span.setAttribute(`${key}.name`, value.name);
                                            span.recordException(value);
                                        } else {
                                            span.setAttribute(key, JSON.stringify(value));
                                        }
                                    } else {
                                        span.setAttribute(key, String(value));
                                    }
                                }
                            });
                        }

                        // Set event name as span name attribute
                        span.setAttribute('event.name', eventName);

                        // Mark span as event (not a full operation)
                        span.setAttribute('event.type', 'custom');
                    } finally {
                        span.end();
                    }
                },
                trackError: (eventName: string, error: unknown) => {
                    // Create a span for the error event
                    const span = tracer.startSpan(eventName);

                    try {
                        span.setAttribute('event.name', eventName);
                        span.setAttribute('event.type', 'error');
                        span.setAttribute('error', true);

                        // Handle different error formats
                        if (error instanceof Error) {
                            span.setAttribute('error.message', error.message);
                            span.setAttribute('error.name', error.name);
                            span.recordException(error);
                        } else if (typeof error === 'object' && error !== null) {
                            // Check if it's an error object with additional context (e.g., { source, error, latencyMs })
                            const errorObj = error as Record<string, unknown>;

                            // Extract error if nested
                            const actualError = errorObj.error;
                            if (actualError instanceof Error) {
                                span.setAttribute('error.message', actualError.message);
                                span.setAttribute('error.name', actualError.name);
                                span.recordException(actualError);
                            } else if (errorObj.message) {
                                span.setAttribute('error.message', String(errorObj.message));
                            }

                            // Add other context attributes
                            Object.entries(errorObj).forEach(([key, value]) => {
                                if (key !== 'error' && value !== null && value !== undefined) {
                                    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                                        span.setAttribute(key, value);
                                    } else if (typeof value === 'object') {
                                        span.setAttribute(key, JSON.stringify(value));
                                    } else {
                                        span.setAttribute(key, String(value));
                                    }
                                }
                            });
                        } else {
                            span.setAttribute('error.message', String(error));
                        }
                    } finally {
                        span.end();
                    }
                }
            };
        },
        []
    );

    return (
        <TelemetryContextProvider value={contextValue}>
            {children}
        </TelemetryContextProvider>
    );
};